<template>
  <VueFinalModal
    :model-value="localVisible"
    @update:model-value="onUpdateModelValue"
    :click-to-close="clickToClose"
    :esc-to-close="clickToClose"
    :content-class="['cm-modal-content', $attrs.class]"
    :content-style="$attrs.style"
    @closed="onClosed"
    @opened="$emit('opened', $event)"
  >
    <slot />
  </VueFinalModal>
</template>

<script>
import { VueFinalModal } from 'vue-final-modal'
import { getModalEntry } from '@/utils/modal'

// Drop-in replacement for vue-js-modal's <modal name="...">.
// See utils/modal.js for the controller backing this.
//
// VFM's `open()` can return falsy on the first watcher tick (its
// internal refs aren't mounted yet on a freshly-rendered modal) and
// emit `update:modelValue=false` to revert. If we bound `entry.visible`
// directly via v-model, that revert would cancel a fresh
// `$modal.show()` before the modal ever renders — this is what was
// happening with PayrollSummary / LeaveBalances on the Reports view.
// We mirror `entry.visible` into `localVisible` (entry → local) and
// only flip back on the authoritative `closed` event.
export default {
  name: 'Modal',
  components: { VueFinalModal },
  inheritAttrs: false,
  props: {
    name: { type: String, required: true },
    clickToClose: { type: Boolean, default: true },
    width: { type: [String, Number], default: 'auto' },
    height: { type: [String, Number], default: 'auto' },
    scrollable: { type: Boolean, default: false },
    adaptive: { type: Boolean, default: false },
  },
  emits: ['before-open', 'opened', 'closed'],
  data() {
    return {
      entry: getModalEntry(this.name),
      localVisible: false,
    }
  },
  watch: {
    'entry.visible': {
      immediate: true,
      handler(now, was) {
        this.localVisible = now
        // vue-js-modal contract: before-open fires when transitioning
        // to visible, with { params } carrying $modal.show()'s payload.
        if (now && !was) {
          this.$emit('before-open', { params: this.entry.params || {} })
        }
      },
    },
  },
  methods: {
    onUpdateModelValue(next) {
      // Only forward true→ transitions. VFM's spurious false-emit on
      // first open is ignored; user-driven close is handled via the
      // `closed` event below.
      if (next) this.localVisible = true
    },
    onClosed(event) {
      this.localVisible = false
      this.entry.visible = false
      this.$emit('closed', event)
    },
  },
}
</script>

<style>
/* Default content styling — pages override via class/style passthrough. */
.cm-modal-content {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  margin: auto;
  background: #fff;
  border-radius: 6px;
  overflow: auto;
}
</style>
